import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useContacts, useExportContactsVCard } from '@/api/hooks';
import { QueryBoundary } from '@/api/QueryBoundary';
import { AppFrame } from '@/design-system/chrome/AppFrame';
import { SideNav, SideNavItem, SideNavSection } from '@/design-system/chrome/SideNav';
import { Icon } from '@/design-system/icon/Icon';
import { Avatar } from '@/design-system/primitives/Avatar/Avatar';
import { Btn } from '@/design-system/primitives/Btn/Btn';
import { Card } from '@/design-system/primitives/Card/Card';
import { Pill } from '@/design-system/primitives/Pill/Pill';
import { hueForName, toNumber } from '@/lib/format';
import { ContactCreateDialog } from './ContactFormDialog';
import styles from './Contacts.module.css';

const PHASE_FILTERS = ['Alle fases', 'Ruwbouw', 'W&W-d.', 'Technieken', 'Afwerking'];

export function Contacts() {
  const contacts = useContacts({ pageSize: 50 });
  const exportVCard = useExportContactsVCard();
  const [createOpen, setCreateOpen] = useState(false);

  return (
    <AppFrame>
      <QueryBoundary query={contacts} loading="Contacten laden…">
        {(page) => {
          const roleCounts = new Map<string, number>();
          for (const c of page.items) {
            roleCounts.set(c.role, (roleCounts.get(c.role) ?? 0) + 1);
          }
          const roles = Array.from(roleCounts.entries()).sort((a, b) => b[1] - a[1]);

          return (
            <>
              <div className={`row ${styles.headerRow}`}>
                <div>
                  <div className="eyebrow">
                    Adresboek · {toNumber(page.total)} contacten · {roles.length} rollen
                  </div>
                  <h1 className={styles.title}>Contacten</h1>
                </div>
                <div className={`ml-auto row gap-8 ${styles.headerActions}`}>
                  <Btn>
                    <Icon name="filter" size={14} /> Rol
                  </Btn>
                  <Btn onClick={() => exportVCard.mutate()} disabled={exportVCard.isPending}>
                    <Icon name="download" size={14} />{' '}
                    {exportVCard.isPending ? 'Bezig…' : 'Exporteer vCard'}
                  </Btn>
                  <Btn variant="primary" onClick={() => setCreateOpen(true)}>
                    <Icon name="plus" size={14} /> Contact
                  </Btn>
                </div>
              </div>
              <ContactCreateDialog open={createOpen} onOpenChange={setCreateOpen} />

              <div className={styles.body}>
                <SideNav ariaLabel="Contacten filters">
                  <SideNavSection>Rol</SideNavSection>
                  <SideNavItem
                    label="Alle"
                    icon="users"
                    active
                    count={toNumber(page.total)}
                  />
                  {roles.map(([role, count]) => (
                    <SideNavItem key={role} label={role} icon="users" count={count} />
                  ))}
                  <SideNavSection>Fase</SideNavSection>
                  {PHASE_FILTERS.map((n) => (
                    <SideNavItem key={n} label={n} icon="flag" />
                  ))}
                </SideNav>

                <Card className={styles.listCard}>
                  <div className={`row gap-8 ${styles.toolbar}`}>
                    <div className={`row gap-6 ${styles.search}`}>
                      <Icon name="search" size={13} />
                      <span className={styles.searchText}>
                        Zoek naam, bedrijf, telefoon, e-mail…
                      </span>
                    </div>
                    <Pill kind="idle" dot={false}>
                      A–Z
                    </Pill>
                  </div>

                  <div className={styles.grid}>
                    {page.items.map((c) => (
                      <Link key={c.id} to={`/contacten/${c.id}`} className={styles.cell}>
                        <div className="row gap-10">
                          <Avatar name={c.name} size={40} hue={hueForName(c.name)} />
                          <div className={`flex-1 ${styles.cellHead}`}>
                            <div className="row gap-6">
                              <div className={styles.contactName}>{c.name}</div>
                            </div>
                            <div className={styles.contactCo}>{c.company ?? c.role}</div>
                          </div>
                        </div>
                        <div className={styles.rolePill}>
                          <div className={`eyebrow ${styles.roleEyebrow}`}>{c.role}</div>
                        </div>
                        <div className={`row gap-10 ${styles.cellFoot}`}>
                          {c.email && (
                            <div className="row gap-4">
                              <Icon name="mail" size={11} />
                              <span className="mono">{c.email.split('@')[0]}</span>
                            </div>
                          )}
                          {c.phone && (
                            <div className={`row gap-4 ${styles.tel}`}>
                              <Icon name="phone" size={11} />
                              <span className={`mono ${styles.telNum}`}>{c.phone}</span>
                            </div>
                          )}
                        </div>
                      </Link>
                    ))}
                  </div>
                </Card>
              </div>
            </>
          );
        }}
      </QueryBoundary>
    </AppFrame>
  );
}
